import { useCallback, useEffect, useState } from "react";
import { Editor } from "@tiptap/react";
import { toast } from "sonner";
import { useUniversalAuth } from "@wtc/ui";
import { AttachmentData, AttachedFileData } from "../types";

/**
 * Hook for managing file attachments in the rich text editor
 * Handles file uploads, Google Drive integration, and attachment state
 */
export function useEditorAttachments(
  editor: Editor | null,
  disabled: boolean,
  isSecondAccount: boolean,
  onAttachmentsChange?: (attachments: AttachmentData[]) => void
) {
  const [attachedFiles, setAttachedFiles] = useState<Map<string, AttachedFileData>>(new Map());
  const [isUploading, setIsUploading] = useState(false);
  const { session } = useUniversalAuth();

  const handleFileAttachment = useCallback(async () => {
    if (!editor || disabled || isUploading) return;

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;

    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length === 0) return;

      // Validate file sizes (max 25MB per file)
      const maxSize = 25 * 1024 * 1024; // 25MB
      for (const file of files) {
        if (file.size > maxSize) {
          toast.error(`File "${file.name}" is too large. Maximum size is 25MB.`);
          return;
        }
      }

      setIsUploading(true);
      try {
        for (const file of files) {
          // Convert file to base64
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          // Generate unique attachment ID
          const attachmentId = `attachment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

          // Store the file content for later use
          setAttachedFiles((prev) =>
            new Map(prev).set(attachmentId, {
              file: { name: file.name, type: file.type },
              content: base64.split(",")[1], // Remove data:...;base64, prefix
              blob: file,
            })
          );
        }

        toast.success(`${files.length} file(s) attached successfully`);
      } catch (error: any) {
        console.error("File attachment error:", error);
        toast.error(`Failed to attach files: ${error.message}`);
      } finally {
        setIsUploading(false);
      }
    };

    input.click();
  }, [editor, disabled, isUploading]);

  const handleGoogleDriveFileSelect = useCallback(
    async (file: any) => {
      if (!editor || disabled || isUploading) return;

      setIsUploading(true);
      try {
        // Download the file from Google Drive
        const params = new URLSearchParams({
          fileId: file.id,
          isSecondAccount: isSecondAccount.toString(),
        });

        // For Google Docs, add export format parameter
        if (file.isGoogleDoc) {
          let defaultFormat = "pdf";
          if (file.mimeType === "application/vnd.google-apps.document") {
            defaultFormat = "docx";
          } else if (file.mimeType === "application/vnd.google-apps.spreadsheet") {
            defaultFormat = "xlsx";
          } else if (file.mimeType === "application/vnd.google-apps.presentation") {
            defaultFormat = "pptx";
          }
          params.set("exportFormat", defaultFormat);
        }

        const response = await fetch(`/api/unifiedtime/getDrive/download?${params.toString()}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session?.access_token || ""}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Download failed: ${response.statusText}`);
        }

        // Get the file blob
        const blob = await response.blob();

        // Convert to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Determine file extension
        let fileName = file.name;
        if (file.isGoogleDoc) {
          let extension = ".pdf";
          if (file.mimeType === "application/vnd.google-apps.document") {
            extension = ".docx";
          } else if (file.mimeType === "application/vnd.google-apps.spreadsheet") {
            extension = ".xlsx";
          } else if (file.mimeType === "application/vnd.google-apps.presentation") {
            extension = ".pptx";
          }
          fileName = file.name.replace(/\.[^/.]+$/, "") + extension;
        }

        // Generate unique attachment ID
        const attachmentId = `attachment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Store the file content for later use
        setAttachedFiles((prev) =>
          new Map(prev).set(attachmentId, {
            file,
            content: base64.split(",")[1], // Remove data:...;base64, prefix
            blob,
          })
        );

        toast.success(`File attached: ${fileName}`);
      } catch (error: any) {
        console.error("Google Drive file attachment error:", error);
        toast.error(`Failed to attach file: ${error.message}`);
      } finally {
        setIsUploading(false);
      }
    },
    [editor, disabled, isUploading, isSecondAccount, session?.access_token]
  );

  const handleAttachmentRemove = useCallback((attachmentId: string) => {
    setAttachedFiles((prev) => {
      const newMap = new Map(prev);
      newMap.delete(attachmentId);
      return newMap;
    });

    toast.success("Attachment removed");
  }, []);

  const getAttachedFiles = useCallback(() => {
    return Array.from(attachedFiles.entries()).map(([id, data]) => ({
      id,
      filename: data.file.name,
      contentType: data.blob.type,
      content: data.content,
      size: data.blob.size,
    }));
  }, [attachedFiles]);

  // Notify parent component when attachments change
  useEffect(() => {
    if (onAttachmentsChange) {
      const files = Array.from(attachedFiles.entries()).map(([id, data]) => ({
        id,
        filename: data.file.name,
        contentType: data.blob.type,
        content: data.content,
        size: data.blob.size,
      }));
      onAttachmentsChange(files);
    }
  }, [attachedFiles, onAttachmentsChange]);

  return {
    attachedFiles,
    isUploading,
    handleFileAttachment,
    handleGoogleDriveFileSelect,
    handleAttachmentRemove,
    getAttachedFiles,
  };
}
